import axios from 'axios';
import type { AxiosAdapter, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

// Axios 1.11 implements these subscription methods but omits them from its
// public CancelToken interface. Use the same removable subscription as XHR.
type ObservableCancelToken = NonNullable<InternalAxiosRequestConfig['cancelToken']> & {
  subscribe: (listener: () => void) => void;
  unsubscribe: (listener: () => void) => void;
};

/**
 * Enforce a dispatch deadline independently of XHR/native cancellation support.
 * This is a single dispatch, never a retry. A timed-out mutation's server outcome
 * is unknown; payment-owned reconciliation retains the original request and key.
 */
export function withRequestDeadline(adapter: AxiosAdapter): AxiosAdapter {
  return (config) =>
    new Promise<AxiosResponse>((resolve, reject) => {
      const controller = new AbortController();
      const callerSignal = config.signal;
      const cancelToken = config.cancelToken as ObservableCancelToken | undefined;
      const timeoutMs = config.timeout ?? 0;
      let timer: ReturnType<typeof setTimeout> | undefined;
      let settled = false;

      const cleanup = () => {
        if (timer !== undefined) clearTimeout(timer);
        callerSignal?.removeEventListener?.('abort', cancel);
        cancelToken?.unsubscribe(cancel);
      };
      const fail = (error: unknown, abort = false) => {
        if (settled) return;
        settled = true;
        cleanup();
        // Win the promise before aborting the adapter: its cancellation rejection
        // must not replace ECONNABORTED/ETIMEDOUT with ERR_CANCELED.
        reject(error);
        if (abort) controller.abort();
      };
      const cancel = () => {
        const error = new axios.CanceledError('canceled');
        error.config = config;
        fail(error, true);
      };

      callerSignal?.addEventListener?.('abort', cancel, { once: true });
      cancelToken?.subscribe(cancel);
      if (callerSignal?.aborted || config.cancelToken?.reason) cancel();
      if (settled) return;

      if (Number.isFinite(timeoutMs) && timeoutMs > 0) {
        timer = setTimeout(() => {
          fail(
            new axios.AxiosError(
              config.timeoutErrorMessage || `timeout of ${timeoutMs}ms exceeded`,
              config.transitional?.clarifyTimeoutError ? 'ETIMEDOUT' : 'ECONNABORTED',
              config
            ),
            true
          );
        }, timeoutMs);
      }

      // Do not abort config.signal itself. Axios checks that original signal after
      // adapter rejection and would otherwise misclassify our timeout as cancellation.
      const transportConfig: InternalAxiosRequestConfig = {
        ...config,
        signal: controller.signal,
        onUploadProgress: config.onUploadProgress
          ? (event) => {
              if (!settled) config.onUploadProgress?.(event);
            }
          : undefined,
        onDownloadProgress: config.onDownloadProgress
          ? (event) => {
              if (!settled) config.onDownloadProgress?.(event);
            }
          : undefined,
      };
      try {
        adapter(transportConfig).then(
          (response) => {
            if (settled) return;
            settled = true;
            cleanup();
            resolve({ ...response, config });
          },
          (error) => fail(error)
        );
      } catch (error) {
        fail(error);
      }
    });
}

/** Install only on shared API-helper clients, never on provider SDK clients. */
export function installRequestDeadline(
  instance: AxiosInstance,
  isFirstParty: (config: InternalAxiosRequestConfig) => boolean
): AxiosInstance {
  if (!instance.interceptors?.request) return instance;
  instance.interceptors.request.use((config) => {
    if (isFirstParty(config)) {
      config.adapter = withRequestDeadline(
        axios.getAdapter(config.adapter ?? instance.defaults.adapter)
      );
    }
    return config;
  });
  return instance;
}
